import { useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  CurrencyInput,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "../../../../components";
import { unformatPrice } from "../../../../utils";
import { InputErros, value } from "../../../../helpers";
import { tooltipStrings } from "./dados-bancarios.utils";
import TextFieldDecimalNumbers from "../../../../components/textFieldDecimalNumbers/textFieldDecimalNumbers.component";
import convenioValidator from "../middlewares/convenio.validator";
import shortid from "shortid";
import {
  BancosCaixasConveniosService,
  notification,
  PlugBoletoService,
} from "../../../../services";
import { BancoConvenio } from "../../entities";
const {
  NUMERO_REMESSA_DINAMICO,
  EXIBIR_TAXA_VENCIMENTO,
  REGISTRO_INSTANTANEO,
} = tooltipStrings;

const convenioService = new BancosCaixasConveniosService();
const plugBoletoService = new PlugBoletoService();

export const AddConvenioDialog = ({
  open,
  setOpen,
  conta,
  convenio,
  setConvenio,
  setListaConvenios,
  dadosEmpresa,
  setLoading,
}) => {
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  const onChangeConvenioInput = (name, value) => {
    setConvenio((prev) => ({
      ...prev,
      [name]: value ? value : undefined,
    }));
  };

  async function atualizarInformacoesIntegrador() {
    setLoading(true);
    const bancoConvenio = new BancoConvenio(convenio, conta.integradorContaId);
    const apiResponseConvenio = await plugBoletoService.atualizarConvenio(
      convenio.integradorConvenioId,
      bancoConvenio,
      dadosEmpresa.cnpjCpf,
    );
    if (!apiResponseConvenio || apiResponseConvenio._status === "erro") {
      throw new Error(
        apiResponseConvenio?._dados?.[0]?._erro ?? "Erro interno de servidor",
      );
    }
    const resultUpdate = await convenioService.atualizar(convenio.id, {
      ...convenio,
      bancoCaixaId: conta.id,
    });
    if (resultUpdate.isAxiosError) {
      throw new Error("Erro interno de servidor");
    }
    setLoading(false);
    notification.sucessoGenericos("Convênio atualizado com sucesso");
  }

  async function cadastrarInformacoesIntegrador() {
    setLoading(true);
    const bancoConvenio = new BancoConvenio(convenio, conta.integradorContaId);
    const apiResponseConvenio = await plugBoletoService.cadastrarConvenio(
      bancoConvenio,
      dadosEmpresa.cnpjCpf,
    );
    if (!apiResponseConvenio || apiResponseConvenio._status === "erro") {
      throw new Error(
        apiResponseConvenio?._dados?.[0]?._erro ?? "Erro interno de servidor",
      );
    }
    const resultCreate = await convenioService.cadastrar({
      ...convenio,
      bancoCaixaId: conta.id,
      integradorConvenioId: apiResponseConvenio._dados.id,
    });
    if (resultCreate.isAxiosError) {
      throw new Error("Erro interno de servidor");
    }

    setLoading(false);
    notification.sucessoGenericos("Convênio cadastrado com sucesso");
    return {
      id: resultCreate.data.id,
      integradorConvenioId: apiResponseConvenio._dados.id,
    };
  }

  const handleAdicionarConvenio = () => {
    convenioValidator
      .validate(convenio, { abortEarly: false })
      .then(async () => {
        if (convenio.id) {
          try {
            if (conta.boletoHabilitado) {
              await atualizarInformacoesIntegrador();
            }
            setListaConvenios((prev) => {
              const conveniosFiltrados = prev.filter(
                (item) => item.id !== convenio.id,
              );
              return [convenio, ...conveniosFiltrados];
            });
            handleCloseDialog();
          } catch (error) {
            notification.erroGenericos(error.message);
            setLoading(false);
          }
        } else {
          try {
            const convenioTemp = { id: shortid.generate(), ...convenio };
            if (conta.boletoHabilitado) {
              const { id, integradorConvenioId } =
                await cadastrarInformacoesIntegrador();
              convenioTemp.id = id;
              convenioTemp.integradorConvenioId = integradorConvenioId;
            }
            setListaConvenios((prev) => [convenioTemp, ...prev]);
            handleCloseDialog();
          } catch (error) {
            notification.erroGenericos(error.message);
            setLoading(false);
          }
        }
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  function handleCloseDialog() {
    setConvenio({});
    inputErros.set(null);
    setOpen(false);
  }

  function handleCheckboxChange(event, name) {
    const { checked } = event.target;
    setConvenio((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  }

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth
      maxWidth="xl"
      PaperComponent="div"
      open={open}
      onClose={handleCloseDialog}
      aria-labelledby="form-dialog-title"
    >
      <DialogContent>
        <Card className="p-3">
          <CardHeader
            title="Cadastro de Convênio"
            action={
              <div className="d-flex align-items-center mx-2">
                <FormControlLabel
                  control={
                    <Switch
                      checked={convenio.boletoHibrido ?? false}
                      onChange={() =>
                        setConvenio((prevState) => ({
                          ...prevState,
                          boletoHibrido: !convenio?.boletoHibrido,
                        }))
                      }
                      inputProps={{ "aria-label": "controlled" }}
                      name="boletoHibrido"
                      color="primary"
                    />
                  }
                  label={
                    convenio?.boletoHibrido
                      ? "Boleto Híbrido"
                      : "Ativar Boleto com PIX"
                  }
                />
              </div>
            }
          />
          <CardContent className="p-3">
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  id="convenioNumero"
                  name="convenioNumero"
                  label="Número do Convênio"
                  variant="outlined"
                  error={inputErros.get("numero")}
                  value={convenio.numero ? convenio.numero : ""}
                  onChange={(e) =>
                    onChangeConvenioInput("numero", e.target.value)
                  }
                  margin="normal"
                  type="number"
                  required
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioDescricao"
                  name="convenioDescricao"
                  label="Descrição"
                  variant="outlined"
                  error={inputErros.get("descricao")}
                  value={convenio.descricao ? convenio.descricao : ""}
                  onChange={(e) =>
                    onChangeConvenioInput("descricao", e.target.value)
                  }
                  margin="normal"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioCarteira"
                  name="convenioCarteira"
                  label="Carteira"
                  variant="outlined"
                  error={inputErros.get("carteira")}
                  value={convenio.carteira ? convenio.carteira : ""}
                  onChange={(e) =>
                    onChangeConvenioInput("carteira", e.target.value)
                  }
                  margin="normal"
                  type="number"
                  required
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioVariacaoCarteira"
                  name="convenioVariacaoCarteira"
                  label="Variação da Carteira"
                  variant="outlined"
                  error={inputErros.get("variacaoCarteira")}
                  value={
                    convenio.variacaoCarteira ? convenio.variacaoCarteira : ""
                  }
                  onChange={(e) =>
                    onChangeConvenioInput("variacaoCarteira", e.target.value)
                  }
                  margin="normal"
                  type="number"
                  required
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3}>
                <Select
                  label="Código da Carteira"
                  name="convenioCodigoCarteira"
                  id="convenioCodigoCarteira"
                  variant="outlined"
                  margin="normal"
                  onChange={(e) =>
                    onChangeConvenioInput("codigoCarteira", e.target.value)
                  }
                  value={convenio?.codigoCarteira}
                  InputLabelProps={{ shrink: true }}
                  error={inputErros.get("codigoCarteira")}
                  disableClearable
                >
                  <MenuItem value={"1"}>1 - Cobrança Simples</MenuItem>
                  <MenuItem value={"2"}>2 - Cobrança Vinculada</MenuItem>
                  <MenuItem value={"3"}>3 - Cobrança Caucionada</MenuItem>
                  <MenuItem value={"4"}>4 - Cobrança Descontada</MenuItem>
                  <MenuItem value={"5"}>5 - Cobrança Vendor</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioNumeroInicial"
                  name="convenioNumeroInicial"
                  label="Nosso Número Inicial"
                  variant="outlined"
                  error={inputErros.get("nossoNumeroInicial")}
                  value={
                    convenio.nossoNumeroInicial
                      ? convenio.nossoNumeroInicial
                      : ""
                  }
                  onChange={(e) =>
                    onChangeConvenioInput("nossoNumeroInicial", e.target.value)
                  }
                  margin="normal"
                  type="number"
                  required
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioNumeroRemessa"
                  name="convenioNumeroRemessa"
                  label="Número da Remessa Inicial"
                  variant="outlined"
                  error={inputErros.get("numeroRemessaInicial")}
                  value={
                    convenio.numeroRemessaInicial
                      ? convenio.numeroRemessaInicial
                      : ""
                  }
                  onChange={(e) =>
                    onChangeConvenioInput(
                      "numeroRemessaInicial",
                      e.target.value,
                    )
                  }
                  margin="normal"
                  type="number"
                  required
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3} container alignItems="center">
                <Tooltip
                  title={
                    <Typography
                      sx={{
                        textAlign: "justify",
                        lineHeight: 1.2,
                        fontSize: "0.85rem",
                        color: "white",
                        p: 1,
                      }}
                    >
                      {NUMERO_REMESSA_DINAMICO}
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          convenio?.numeroRemessaDinamico
                            ? convenio.numeroRemessaDinamico
                            : false
                        }
                        value={convenio?.numeroRemessaDinamico}
                        onChange={(e) =>
                          handleCheckboxChange(e, "numeroRemessaDinamico")
                        }
                        name="convenioNumeroRemessaDinamico"
                        color="primary"
                      />
                    }
                    label="Número de remessa dinâmico"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={3}>
                <Select
                  label="Padrão CNAB"
                  name="convenioPadraoCnab"
                  id="convenioPadraoCnab"
                  variant="outlined"
                  margin="normal"
                  onChange={(e) =>
                    onChangeConvenioInput("padraoCnab", e.target.value)
                  }
                  value={convenio?.padraoCnab}
                  InputLabelProps={{ shrink: true }}
                  error={inputErros.get("padraoCnab")}
                  disableClearable
                  required
                >
                  <MenuItem value={"240"}>CNAB 240</MenuItem>
                  <MenuItem value={"400"}>CNAB 400</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioNumeroContrato"
                  name="convenioNumeroContrato"
                  label="Número do Contrato"
                  variant="outlined"
                  error={inputErros.get("numeroContrato")}
                  value={convenio.numeroContrato ? convenio.numeroContrato : ""}
                  onChange={(e) =>
                    onChangeConvenioInput("numeroContrato", e.target.value)
                  }
                  margin="normal"
                  type="number"
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3}>
                <Select
                  label="Código de Baixa Devolução"
                  name="convenioCodigoBaixaDevolucao"
                  id="convenioCodigoBaixaDevolucao"
                  variant="outlined"
                  margin="normal"
                  onChange={(e) =>
                    onChangeConvenioInput(
                      "codigoBaixaDevolucao",
                      e.target.value,
                    )
                  }
                  value={convenio?.codigoBaixaDevolucao}
                  InputLabelProps={{ shrink: true }}
                  disableClearable
                >
                  <MenuItem value={"1"}>Baixar / Devolver</MenuItem>
                  <MenuItem value={"2"}>Não baixar / Não devolver</MenuItem>
                  <MenuItem value={"3"}>
                    Cancelar prazo para baixo / devolução
                  </MenuItem>
                </Select>
              </Grid>
              <Grid item xs={3} container alignItems="center">
                <Tooltip
                  title={
                    <Typography
                      sx={{
                        textAlign: "justify",
                        lineHeight: 1.2,
                        fontSize: "0.85rem",
                        color: "white",
                        p: 1,
                      }}
                    >
                      {REGISTRO_INSTANTANEO}
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          convenio?.registroInstantaneo
                            ? convenio.registroInstantaneo
                            : false
                        }
                        value={convenio?.registroInstantaneo}
                        onChange={(e) =>
                          handleCheckboxChange(e, "registroInstantaneo")
                        }
                        name="convenioRegistroInstantaneo"
                        color="primary"
                      />
                    }
                    label="Registro instantâneo"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioPrazoBaixa"
                  name="convenioPrazoBaixa"
                  label="Prazo de Baixa"
                  variant="outlined"
                  value={convenio.prazoBaixa ? convenio.prazoBaixa : ""}
                  onChange={(e) =>
                    onChangeConvenioInput("prazoBaixa", e.target.value)
                  }
                  margin="normal"
                  type="number"
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3}>
                <TextFieldDecimalNumbers
                  id="convenioTaxaJuros"
                  name="convenioTaxaJuros"
                  label="Taxa de juros (%)"
                  variant="outlined"
                  margin="normal"
                  value={convenio.taxaJuros || ""}
                  onBlur={(e) => {
                    onChangeConvenioInput(
                      "taxaJuros",
                      unformatPrice(e.target.value),
                    );
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <CurrencyInput
                  id="convenioValorMulta"
                  name="convenioValorMulta"
                  label="Valor da multa (R$)"
                  variant="outlined"
                  margin="normal"
                  value={convenio.valorMulta || ""}
                  onBlur={(e) => {
                    onChangeConvenioInput(
                      "valorMulta",
                      unformatPrice(e.target.value),
                    );
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <TextFieldDecimalNumbers
                  id="convenioTaxaMulta"
                  name="convenioTaxaMulta"
                  label="Taxa de multa (%)"
                  variant="outlined"
                  margin="normal"
                  value={convenio.taxaMulta || ""}
                  onBlur={(e) => {
                    onChangeConvenioInput(
                      "taxaMulta",
                      unformatPrice(e.target.value),
                    );
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioDiaLimiteDesconto"
                  name="convenioDiaLimiteDesconto"
                  label="Dia limite para desconto"
                  variant="outlined"
                  value={
                    convenio.diaLimiteDesconto ? convenio.diaLimiteDesconto : ""
                  }
                  onChange={(e) =>
                    onChangeConvenioInput("diaLimiteDesconto", e.target.value)
                  }
                  margin="normal"
                  type="number"
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3}>
                <TextFieldDecimalNumbers
                  id="convenioTaxaDesconto"
                  name="convenioTaxaDesconto"
                  label="Taxa de desconto (%)"
                  variant="outlined"
                  margin="normal"
                  value={convenio.taxaDesconto || ""}
                  onBlur={(e) => {
                    onChangeConvenioInput(
                      "taxaDesconto",
                      unformatPrice(e.target.value),
                    );
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  id="convenioDiasCarencia"
                  name="convenioDiasCarencia"
                  label="Carência (dias)"
                  variant="outlined"
                  value={convenio.carencia ? convenio.carencia : ""}
                  onChange={(e) =>
                    onChangeConvenioInput("carencia", e.target.value)
                  }
                  margin="normal"
                  type="number"
                  fullWidth
                  disableNumberArrows
                />
              </Grid>
              <Grid item xs={3} container alignItems="center">
                <Tooltip
                  title={
                    <Typography
                      sx={{
                        textAlign: "justify",
                        lineHeight: 1.2,
                        fontSize: "0.85rem",
                        color: "white",
                        p: 1,
                      }}
                    >
                      {EXIBIR_TAXA_VENCIMENTO}
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          convenio?.exibirTaxaVencimento
                            ? convenio.exibirTaxaVencimento
                            : false
                        }
                        value={convenio?.exibirTaxaVencimento}
                        onChange={(e) =>
                          handleCheckboxChange(e, "exibirTaxaVencimento")
                        }
                        name="convenioExibirTaxaVencimento"
                        color="primary"
                      />
                    }
                    label="Exibir taxa de vencimento"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  label="Instruções"
                  id="convenioInstrucoes"
                  name="convenioInstrucoes"
                  type="text"
                  margin="normal"
                  fullWidth
                  onChange={(e) =>
                    onChangeConvenioInput("instrucoes", e.target.value)
                  }
                  value={value.text(convenio.instrucoes)}
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions
            className="d-flex justify-content-end gap-2"
            sx={{ margin: "0", marginTop: "8px", marginBottom: "8px" }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdicionarConvenio}
            >
              <i
                className={`ph-fill ph-${
                  convenio?.id ? "check-circle" : "plus-circle"
                }`}
                style={{ color: "#fff", fontSize: 22, marginRight: 10 }}
              />
              {convenio?.id ? "Atualizar" : "Adicionar"}
            </Button>
            <Button
              color="padrao"
              variant="contained"
              onClick={handleCloseDialog}
            >
              <i
                className="ph-fill ph-x-circle"
                style={{ fontSize: 22, marginRight: 10 }}
              />
              Fechar
            </Button>
          </CardActions>
        </Card>
      </DialogContent>
    </Dialog>
  );
};
